/**
  * Purchase success page - shown after successful Stripe checkout
  */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AuroraBackground from '../components/AuroraBackground';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const PurchaseSuccessPage: React.FC = () => {
    const { user, loading, refreshUser, getIdToken } = useAuth();
    const [isRefreshing, setIsRefreshing] = useState(true);
    const [creditsAdded, setCreditsAdded] = useState(500);
    const navigate = useNavigate();

    // Fetch pricing config for dynamic values
    useEffect(() => {
        const fetchPricing = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/config/pricing`);
                if (response.ok) {
                    const data = await response.json();
                    const credits = data.credits;
                    if (credits && credits.batch_credit_amount) {
                        setCreditsAdded(credits.batch_credit_amount);
                    }
                }
            } catch (err) {
                console.error('Error fetching pricing:', err);
            }
        };
        fetchPricing();
    }, []);

    // Sync purchase from Stripe and refresh user data
    useEffect(() => {
        const syncAndRefresh = async () => {
            if (!loading && user) {
                try {
                    // First, sync purchase from Stripe (for local dev without webhooks)
                    const idToken = await getIdToken();
                    if (idToken) {
                        try {
                            const response = await fetch(`${API_BASE_URL}/subscription/sync`, {
                                method: 'POST',
                                headers: {
                                    'Authorization': `Bearer ${idToken}`,
                                    'Content-Type': 'application/json',
                                },
                            });
                            if (response.ok) {
                                const result = await response.json();
                                console.log('Purchase sync result:', result);
                            }
                        } catch (syncErr) {
                            console.error('Error syncing purchase:', syncErr);
                        }
                    }

                    // Then refresh user data
                    await refreshUser();
                } catch (err) {
                    console.error('Error refreshing user:', err);
                }
                setIsRefreshing(false);
            } else if (!loading && !user) {
                navigate('/login');
            }
        };

        syncAndRefresh();
    }, [loading, user, refreshUser, getIdToken, navigate]);

    const handleContinue = () => {
        navigate('/sessions');
    };

    if (loading || isRefreshing) {
        return (
            <>
                <AuroraBackground />
                <div style={styles.container}>
                    <div style={styles.spinner} />
                    <p style={styles.loadingText}>Adding your credits...</p>
                </div>
            </>
        );
    }

    return (
        <>
            <AuroraBackground />
            <div style={styles.container}>
                <div style={styles.card}>
                <div style={styles.iconContainer}>
                    <svg style={styles.icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                        <path d="M22 4L12 14.01l-3-3" />
                    </svg>
                </div>

                <h1 style={styles.title}>Credits Purchased!</h1>
                <p style={styles.message}>
                    {creditsAdded} credits have been added to your account.
                </p>

                <div style={styles.features}>
                    <div style={styles.featureItem}>
                        <svg style={styles.checkIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M5 13l4 4L19 7" />
                        </svg>
                        <span>{creditsAdded} AI-generated comments</span>
                    </div>
                    <div style={styles.featureItem}>
                        <svg style={styles.checkIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M5 13l4 4L19 7" />
                        </svg>
                        <span>Credits never expire</span>
                    </div>
                    <div style={styles.featureItem}>
                        <svg style={styles.checkIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M5 13l4 4L19 7" />
                        </svg>
                        <span>Unlimited Instagram accounts</span>
                    </div>
                </div>

                <button onClick={handleContinue} style={styles.button}>
                    Start Creating Comments
                </button>
            </div>
        </div>
        </>
    );
};

const styles: { [key: string]: React.CSSProperties } = {
    container: {
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
    },
    card: {
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        borderRadius: '16px',
        padding: '48px',
        maxWidth: '480px',
        width: '100%',
        textAlign: 'center',
        border: '1px solid rgba(0, 0, 0, 0.1)',
        boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
    },
    iconContainer: {
        marginBottom: '24px',
    },
    icon: {
        width: '80px',
        height: '80px',
        color: '#10B981',
        margin: '0 auto',
    },
    title: {
        fontSize: '32px',
        fontWeight: 700,
        color: '#000000',
        marginBottom: '12px',
    },
    message: {
        fontSize: '16px',
        color: '#666666',
        marginBottom: '32px',
    },
    features: {
        textAlign: 'left',
        marginBottom: '32px',
    },
    featureItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        color: '#333333',
        fontSize: '14px',
        padding: '12px 0',
        borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
    },
    checkIcon: {
        width: '20px',
        height: '20px',
        color: '#10B981',
        flexShrink: 0,
    },
    button: {
        width: '100%',
        background: '#7C3AED',
        color: '#FFFFFF',
        border: 'none',
        borderRadius: '8px',
        padding: '16px 24px',
        fontSize: '16px',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
    },
    spinner: {
        width: '40px',
        height: '40px',
        border: '3px solid rgba(0, 0, 0, 0.1)',
        borderTopColor: '#7C3AED',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
        marginBottom: '16px',
    },
    loadingText: {
        color: '#666666',
        fontSize: '16px',
    },
};

export default PurchaseSuccessPage;
